import clsx from "clsx";
import React from "react";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

// eslint-disable-next-line no-restricted-imports
import { InputBase, MenuItem, Select, TablePagination, TablePaginationProps } from "@mui/material";
import type { TablePaginationActionsProps } from "@mui/material/TablePaginationActions";
import TablePaginationActions from "@mui/material/TablePaginationActions";

const useStyles = makeStyles()((theme) => ({
    actionsWrapper: {
        display: "flex",
        alignItems: "center",
        flexShrink: 0,
        flexWrap: "nowrap",
        gap: theme.spacing(0.5),
    },
    toolbarLabel: {
        color: theme.palette.emteria.text.primary,
    },
    pageLabel: {
        marginLeft: theme.spacing(2),
    },
    paginationSelect: {
        minWidth: 48,
        marginLeft: theme.spacing(1),
        fontSize: "inherit",
        "& .MuiSelect-select": {
            paddingTop: theme.spacing(0.5),
            paddingBottom: theme.spacing(0.5),
            paddingLeft: theme.spacing(2),
            paddingRight: theme.spacing(6),
            fontSize: "inherit",
            textAlign: "right",
            textAlignLast: "right",
        },
    },
}));

type PageNumberSelectProps = {
    count: number;
    page: number;
    rowsPerPage: number;
    disabled?: boolean;
    onPageChange: TablePaginationActionsProps["onPageChange"];
};

const PageNumberSelect = ({ count, page, rowsPerPage, disabled, onPageChange }: PageNumberSelectProps) => {
    const { t } = useTranslation(["common"]);
    const { classes } = useStyles();
    const labelId = React.useId();
    const totalPages = count > 0 && rowsPerPage > 0 ? Math.ceil(count / rowsPerPage) : 1;

    return (
        <span>
            <span id={labelId} className={clsx(classes.toolbarLabel, classes.pageLabel)}>
                {t("common:table.pagination.pageLabel")}
            </span>
            <Select<number>
                variant="standard"
                input={<InputBase />}
                disabled={disabled || totalPages <= 1}
                value={page}
                onChange={(e) => onPageChange(null, Number(e.target.value))}
                labelId={labelId}
                className={classes.paginationSelect}
                MenuProps={{
                    slotProps: {
                        paper: {
                            style: { maxHeight: 240 },
                        },
                    },
                }}
            >
                {Array.from({ length: totalPages }, (_, i) => (
                    <MenuItem key={i} value={i} dense>
                        {i + 1}
                    </MenuItem>
                ))}
            </Select>
        </span>
    );
};

const PaginationActionsWithPageSelect = React.forwardRef<HTMLDivElement, TablePaginationActionsProps>((props, ref) => {
    const { classes } = useStyles();
    const { className, ...actionsProps } = props;
    return (
        <div ref={ref} className={`${className ?? ""} ${classes.actionsWrapper}`}>
            <TablePaginationActions {...actionsProps} />
            <PageNumberSelect
                count={props.count}
                page={props.page}
                rowsPerPage={props.rowsPerPage}
                disabled={props.disabled}
                onPageChange={props.onPageChange}
            />
        </div>
    );
});
PaginationActionsWithPageSelect.displayName = "PaginationActionsWithPageSelect";

interface CustomTablePaginationProps extends Omit<TablePaginationProps, "slotProps"> {
    slotProps?: TablePaginationProps["slotProps"];
}

const CustomTablePagination: React.FC<CustomTablePaginationProps> = ({
    component = "div",
    slotProps,
    labelRowsPerPage,
    showFirstButton = true,
    showLastButton = true,
    rowsPerPageOptions = [12, 24, 48],
    ...props
}) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["common"]);

    const pageButtonLabels = {
        first: t("common:table.pagination.firstPage"),
        previous: t("common:table.pagination.previousPage"),
        next: t("common:table.pagination.nextPage"),
        last: t("common:table.pagination.lastPage"),
    };

    return (
        <TablePagination
            {...props}
            component={component}
            rowsPerPageOptions={rowsPerPageOptions}
            showFirstButton={showFirstButton}
            showLastButton={showLastButton}
            ActionsComponent={PaginationActionsWithPageSelect}
            getItemAriaLabel={(type) => pageButtonLabels[type]}
            labelDisplayedRows={({ from, to, count }) => t("common:table.pagination.displayedRows", { from, to, count })}
            labelRowsPerPage={labelRowsPerPage || t("common:table.pagination.rows")}
            slotProps={{
                ...slotProps,
                selectLabel: {
                    ...slotProps?.selectLabel,
                    className: classes.toolbarLabel,
                },
                select: {
                    ...slotProps?.select,
                    className: classes.paginationSelect,
                    MenuProps: {
                        ...slotProps?.select?.MenuProps,
                        slotProps: {
                            paper: {
                                style: { maxHeight: 240 },
                            },
                        },
                    },
                },
                menuItem: {
                    dense: true,
                    ...slotProps?.menuItem,
                },
            }}
        />
    );
};

export default CustomTablePagination;
